/**
 * AI Chatbot schema indexing — setup visibility and indexing actions.
 *
 * Standard indexing (public.embeddings): product builder tenants only.
 * Custom indexing (tenant.embeddings): AI Chatbot package per tenant; product builder bypasses package.
 */
import { useEffect, useState } from 'react';
import { getEntitlementsSection, hasPackage } from './entitlements';
import { resolveProductBuilderMode } from './productBuilderMode';
import ObjectConfigApi from '../api/ObjectConfigApi';

export const AICHATBOT_INDEX_STANDARD_FEATURE_KEY = 'aichatbot_index_standard';
export const AICHATBOT_INDEX_CUSTOM_FEATURE_KEY = 'aichatbot_index_custom';

/**
 * Whether Setup should list the AI Chatbot indexing page (package + setup item, or product builder).
 */
export function canShowAiChatbotIndexingInSetup(productBuilderMode = false) {
  if (productBuilderMode === true) return true;
  const setupConfig = getEntitlementsSection('setup', {});
  const allowedItems = (setupConfig?.allowedItems || []).map((i) => String(i).toLowerCase());
  const hasSetupItem = allowedItems.includes('aichatbot');
  return hasSetupItem && hasPackage('aichatbot');
}

/**
 * Fetch indexing capabilities from backend (authoritative).
 * @returns {Promise<{ canAccessSetup: boolean, canIndexStandard: boolean, canIndexCustom: boolean, productBuilder: boolean, hasAichatbotPackage: boolean }>}
 */
export async function fetchAiChatbotIndexingOptions() {
  const res = await ObjectConfigApi.getEmbeddingIndexingOptions();
  if (!res?.success) {
    return {
      canAccessSetup: false,
      canIndexStandard: false,
      canIndexCustom: false,
      productBuilder: false,
      hasAichatbotPackage: hasPackage('aichatbot'),
    };
  }
  return {
    canAccessSetup: res.canAccessSetup === true,
    canIndexStandard: res.canIndexStandard === true,
    canIndexCustom: res.canIndexCustom === true,
    productBuilder: res.productBuilder === true,
    hasAichatbotPackage: res.hasAichatbotPackage === true,
  };
}

/**
 * React hook: resolves whether AI Chatbot indexing should appear in Setup sidebar.
 */
export function useAiChatbotIndexingSetupVisible() {
  const [visible, setVisible] = useState(() => {
    if (hasPackage('aichatbot')) {
      const setupConfig = getEntitlementsSection('setup', {});
      const allowedItems = (setupConfig?.allowedItems || []).map((i) => String(i).toLowerCase());
      if (allowedItems.includes('aichatbot')) return true;
    }
    return false;
  });

  useEffect(() => {
    let cancelled = false;
    resolveProductBuilderMode().then((productBuilder) => {
      if (cancelled) return;
      setVisible(canShowAiChatbotIndexingInSetup(productBuilder));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return visible;
}
